import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useTestRunEditDialog } from '@/features/executions/components/useTestRunEditDialog';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  buildTestDetailPath,
  buildTestRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import {
  TableV2ActionsHeaderIcon,
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import { buildTagColorByName } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import type { TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import type { TagColor } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestRunDto } from '@flowqa/shared';

type TestRunsTableV2Props = {
  active: boolean;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  initialPageSize?: number;
  noRowsText?: string;
  pageSizeOptions?: number[];
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  queryKey: QueryKey;
  search?: string;
  showTestName?: boolean;
  onQueryMeta?: (meta: { total: number; items: TestRunDto[] } | null) => void;
};

const testRunV2Sorts = [{ field: 'timestamp', direction: 'desc' }] satisfies ApiSort[];
const skeletonColumns = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

const colgroup = (
  <colgroup>
    <col width="24%" />
    <col width="11%" />
    <col width="13%" />
    <col width="24%" />
    <col width="22%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const header = (
  <TableRow>
    <TableCell>Test Run</TableCell>
    <TableCell>Status</TableCell>
    <TableCell>Duration</TableCell>
    <TableCell>Ran At</TableCell>
    <TableCell>Tags</TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const TestRunRow = ({
  deleteRowPendingId,
  isLastVisibleRow,
  row,
  showTestName,
  tagColorByName,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  row: TestRunDto;
  showTestName: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestRunDto) => void;
  onEditRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const name = row.displayName ?? row.testName ?? row.name ?? row.id;
  const detailPath = buildTestDetailPath(row.testSlug ?? row.testId);
  const runPath = buildTestRunPath(row.testSlug ?? row.testId, row.runNumber);
  const runNumberLabel = formatTableV2RunNumber(row.runNumber);
  const primaryLabel = showTestName ? name : (runNumberLabel ?? name);
  const primaryHref = showTestName ? detailPath : runPath;
  const secondaryLabel = showTestName ? runNumberLabel : null;
  const secondaryHref = showTestName ? runPath : null;

  return (
    <TableRow sx={buildTableV2BodyRowSurfaceSx({ hasRenderedDivider: !isLastVisibleRow })}>
      <TableCell>
        <TableV2NameCell
          href={primaryHref}
          primary={primaryLabel}
          secondary={secondaryLabel}
          secondaryHref={secondaryHref}
        />
      </TableCell>
      <TableCell>
        <TableV2StatusChip status={row.status} />
      </TableCell>
      <TableCell>
        <TableV2DurationCell value={row.durationMs} />
      </TableCell>
      <TableCell>
        <TableV2DateTimeCell value={row.timestamp} />
      </TableCell>
      <TableCell>
        <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
      </TableCell>
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableActionsCell
          rowName={name}
          verticalAlign="center"
          deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
          editActionLabel={getEntityActionLabel('edit', 'Test Run')}
          editTagsEntityNoun="test run"
          rowTags={row.tags ?? []}
          onEdit={() => {
            onEditRow(row);
          }}
          onUpdateTagsColor={(input) => {
            onUpdateRowTagsColor({ row, ...input });
          }}
          updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
          onDelete={() => {
            onDeleteRow(row);
          }}
          disableDelete={deleteRowPendingId === row.id}
        />
      </TableCell>
    </TableRow>
  );
};

export const TestRunsTableV2 = ({
  active,
  fixedFilters,
  forceLoading = false,
  initialPageSize,
  noRowsText = 'No data',
  onQueryMeta,
  pageSizeOptions,
  queryFn,
  queryKey,
  search,
  showTestName = true,
}: TestRunsTableV2Props): ReactElement => {
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const tagColorByName = buildTagColorByName(tagOptions);
  const { editLoading, editTarget, onEditClose, onEditRow, onEditSave } =
    useTestRunEditDialog(selectedProjectId);
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useTestRunsTableRowActions({ selectedProjectId });

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Test runs"
        colgroup={colgroup}
        emptyText={noRowsText}
        entityLabel="test runs"
        entitySingularLabel="test run"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={header}
        initialPageSize={initialPageSize}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={testRunV2Sorts}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <TestRunRow
            key={row.id}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            row={row}
            showTestName={showTestName}
            tagColorByName={tagColorByName}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onEditRow={onEditRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
          />
        )}
      />
      <TestRunEditDialog
        open={editTarget !== null}
        execution={editTarget}
        loading={editLoading}
        onClose={onEditClose}
        onSave={onEditSave}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
